import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import _ from 'lodash';
import Swal from 'sweetalert2';
import { ICommerce } from 'src/app/models/commerce.model';
import { IProduct } from 'src/app/models/products.model';
import { AppState } from '../app.reducer';
import { Order, IOrder, ICommerceOrder, Concept, TypeOrder } from '../shared/models/order.model';
import { SetOrderActions } from '../shared/reducers/order.actions';
import { EncoderService } from './encoder.service';
import { UtilsService } from './utils.service';
import daysjs, { Dayjs } from 'dayjs';

@Injectable({
	providedIn: 'root'
})
export class ShoppingCartService {

	constructor(private store: Store<AppState>,
		private utils: UtilsService,
		private encoder: EncoderService) { }


	/**
	 * Construye el Concept para la primera unidad de un producto (usado al
	 * crearlo tanto desde addUnit() como desde updateNote()), para no repetir
	 * el mismo objeto literal en los dos sitios.
	 */
	private createConcept(product: IProduct): Concept {
		return new Concept({
			titulo: product.titulo,
			cantidad: 1,
			precio_venta: (product.oferta === true) ? product.precio_oferta : product.precio,
			impuesto: product.impuesto,
			product_key: product.key,
			id: this.utils.makeid(10),
			image: (product.first_image) ? this.getPublicImgUrl(product.first_image) : null,
			formato: product.formato,
			observaciones: product.observaciones || ''
		});
	}

	addUnit(order: IOrder, commerce: ICommerce, product: IProduct) {

		// Verificar si el producto está activo
		if (product.active === false) {
			Swal.fire({
				title: 'Producto no disponible',
				text: 'Este producto ya no está disponible.',
				icon: 'warning'
			});
			return;
		}

		// Verificar stock antes de añadir la primera unidad (el incremento de una unidad
		// ya en el carrito se comprueba más abajo, contra la cantidad actual)
		if (!product.stock || product.stock < 1) {
			Swal.fire({
				title: 'Sin stock',
				text: 'Este producto no tiene unidades disponibles.',
				icon: 'warning'
			});
			return;
		}

		// Todo el trabajo se hace sobre _order (el clon): commerceOrder/concept
		// se leen y mutan siempre a través de él, nunca del "order" original,
		// para no mutar por accidente el pedido que todavía tiene el store/los
		// componentes que llamaron a este método.
		const _order: IOrder = JSON.parse(JSON.stringify(order));
		const commerceOrderIndex = _.findIndex(_order.commerces, ['commerce_key', commerce.key]);

		if (commerceOrderIndex !== -1) {
			const commerceOrder: ICommerceOrder = _order.commerces[commerceOrderIndex];
			const conceptIndex = _.findIndex(commerceOrder.conceptos, ['product_key', product.key]);

			if (conceptIndex !== -1) {
				const concept = commerceOrder.conceptos[conceptIndex];
				if (concept.cantidad >= product.stock) {
					return;
				}
				concept.cantidad = concept.cantidad + 1;
				if (product.observaciones) {
					concept.observaciones = product.observaciones;
				}
				commerceOrder.conceptos[conceptIndex] = new Concept(concept);
			} else {
				commerceOrder.conceptos.push(this.createConcept(product));
			}
		} else {
			const concept = this.createConcept(product);
			_order.commerces.push({
				horario: commerce.horario,
				commerce_key: commerce.key,
				email_comercio: commerce.email,
				nombre_comercio: commerce.nombre_comercio,
				conceptos: [concept],
				base_imponible: concept.base_imponible,
				importe_iva: concept.importe_iva,
				total: concept.total,
				id: this.utils.makeid(10),
				open: true,
				pedido_minimo: commerce.pedido_minimo,
				fecha_recogida: ''
			})
		}

		this.updateTotal(_order);
		const action = new SetOrderActions(_order);
		this.store.dispatch(action);
		localStorage.setItem('order', this.encoder.encode(JSON.stringify(_order)))

	}

	subtractUnit(order: IOrder, commerce: ICommerce, product: IProduct) {

		if (product.cantidad == 0) {
			return;
		}

		const _order = JSON.parse(JSON.stringify(order));
		const commerceOrderIndex = _.findIndex(_order.commerces, ['commerce_key', commerce.key]);
		const commerceOrder: ICommerceOrder = _order.commerces[commerceOrderIndex];

		if (commerceOrder) {
			const conceptIndex = _.findIndex(commerceOrder.conceptos, ['product_key', product.key]);
			if (conceptIndex !== -1) {
				const concept = commerceOrder.conceptos[conceptIndex];
				concept.cantidad = concept.cantidad - 1;
				commerceOrder.conceptos[conceptIndex] = new Concept(concept);
				this.updateTotal(_order);
				const action = new SetOrderActions(_order);
				this.store.dispatch(action);
				localStorage.setItem('order', this.encoder.encode(JSON.stringify(_order)))

			}

		}

	}

	removeConcept(order: IOrder, commerce: ICommerce, product: IProduct) {

		const _order = JSON.parse(JSON.stringify(order));
		const commerceOrderIndex = _.findIndex(_order.commerces, ['commerce_key', commerce.key]);
		const commerceOrder: ICommerceOrder = _order.commerces[commerceOrderIndex];

		if (commerceOrder) {

			const conceptIndex = _.findIndex(commerceOrder.conceptos, ['product_key', product.key]);
			if (conceptIndex !== -1) {

				commerceOrder.conceptos.splice(conceptIndex, 1);

				if (commerceOrder.conceptos.length === 0) {

					_order.commerces.splice(commerceOrderIndex, 1);
					this.updateTotal(_order);
					const action = new SetOrderActions(_order);
					this.store.dispatch(action);
					localStorage.setItem('order', this.encoder.encode(JSON.stringify(_order)));


				} else {
					this.updateTotal(_order);

					const action = new SetOrderActions(_order);
					this.store.dispatch(action);
					localStorage.setItem('order', this.encoder.encode(JSON.stringify(_order)))
				}
			}
		}
	}

	removeCoupon(order, coupon) {

		const _order = JSON.parse(JSON.stringify(order));
		// // console.log(order.coupons);
		_.remove(_order.coupons, (_coupon: any) => {
			return _coupon.key === coupon.key;
		});
		// // console.log(order.coupons);
		// // console.log(_order.coupons);
		const action = new SetOrderActions(_order);
		this.store.dispatch(action);
		localStorage.setItem('order', this.encoder.encode(JSON.stringify(_order)))
	}
	fechaExiste(fechas, nuevaFecha) {
		// // console.log(fechas, nuevaFecha);

		for (const fecha of fechas) {

			if (daysjs(fecha).isSame(daysjs(nuevaFecha))) {
				return true;
			}
		}

		return false;
	}
	addCoupon(order, coupon) {

		const _order = JSON.parse(JSON.stringify(order));

		if (!_order.coupons) {
			_order.coupons = [];
		}

		const object = _.find(order.coupons, ['titulo', coupon.titulo]);
		if (object) {
			Swal.fire('Ya se ha añadido el cupón', '', 'error');
			return;
		}
		_order.coupons.push(coupon);
		// // console.log(coupon);
		// // console.log(_order.coupons);
		const action = new SetOrderActions(_order);
		this.store.dispatch(action);
		localStorage.setItem('order', this.encoder.encode(JSON.stringify(_order)))
	}

	updateTotal(order: IOrder) {
		// // console.log('ENTRAS EN UPDATETOTAL', order.total);
		const fechaRepartos = [];
		order.total = 0;
		order.descuento = 0;
		order.total_compra = 0;
		order.base_imponible = 0;
		order.importe_iva = 0;

		order.commerces.map(commerce => {
			commerce.total = 0;
			// commerce.pedido_diferencia = commerce.pedido_minimo;
			commerce.base_imponible = 0;
			commerce.importe_iva = 0;
			commerce.descuento = 0;
			commerce.conceptos.map(concept => {

				concept.descuento = 0;
				// concept.base_imponible = parseFloat((((concept.cantidad * concept.precio_venta) / ((100 + concept.impuesto) / 100))).toFixed(2));
				concept.total = parseFloat((concept.cantidad * concept.precio_venta).toFixed(2))

				if (order.coupons.length > 0) {
					// // console.log('ENTRAS EN COMMERCEKEY1 ' + concept.titulo + ' base_imponible: ', concept.base_imponible)

					order.coupons.map(item => {
						// const commerce_index = _.findIndex(order.commerces, ['commerce_key', item.comercio]);
						// const commerce = _.find(order.commerces, ['commerce_key', item.comercio]);
						// // // console.log('ENTRAS EN COMMERCEKEY1')



						if (commerce.commerce_key === item.comercio) {
							if (item.tipo_descuento === 'porcentaje') {
								const descuento = parseFloat(((concept.total * item.descuento / 100)).toFixed(2));
								concept.descuento += descuento;
							}

							if (item.tipo_descuento === 'numero') {
								const descuento = concept.total - item.descuento;
								concept.descuento += descuento;
								// concept.base_imponible = concept.base_imponible - item.descuento;
							}
							// // console.log('ENTRAS EN COMMERCEKEY2 ' + concept.titulo + ' DESCUENTO: ', concept.descuento)
							// // console.log('ENTRAS EN COMMERCEKEY2 ' + concept.titulo + ' base_imponible: ', concept.base_imponible)

						}
					})
					// order.total -= order.descuento;
					// order.total_compra -= order.descuento;
				}
				// concept.total = concept.cantidad * concept.precio_venta;
				// concept.importe_iva = concept.total - concept.base_imponible;
				concept.total = parseFloat((concept.total - concept.descuento).toFixed(2));
				concept.importe_iva = parseFloat((concept.total * concept.impuesto / 100).toFixed(2));
				concept.base_imponible = parseFloat((concept.total - concept.importe_iva).toFixed(2));
				// // console.log('concept', concept);
				commerce.base_imponible += concept.base_imponible;
				commerce.importe_iva += concept.importe_iva;
				commerce.total += concept.total;
				commerce.descuento += concept.descuento;
				order.base_imponible += concept.base_imponible;
				order.importe_iva += concept.importe_iva;
				order.total += concept.total;
				order.total_compra += concept.total;
				order.descuento += concept.descuento;
			})

			commerce.pedido_diferencia = commerce.pedido_minimo - commerce.total;
			// // console.log(commerce.pedido_minimo, commerce.total, commerce.pedido_diferencia)
			if (commerce.pedido_diferencia < 0) {
				commerce.pedido_diferencia = 0;
			}

			if (commerce.fecha_recogida && !this.fechaExiste(fechaRepartos, commerce.fecha_recogida)) {
				fechaRepartos.push(commerce.fecha_recogida)
			}
		})

		order.gastos_envio = Math.round(2.95 * (fechaRepartos.length === 0 ? 1 : fechaRepartos.length) * 100) / 100
		// console.log("gastos_envio", order.gastos_envio);
		if (order.commerces.length > 0) {
			order.available_for_purchase = true;

		} else {
			order.available_for_purchase = false;
		}

		order.commerces.map(item => {
			if (item.pedido_diferencia > 0) {
				order.available_for_purchase = false;
			}
		})
		// if (order.coupons.length > 0) {

		//   order.coupons.map(item => {
		//     const commerce_index = _.findIndex(order.commerces, ['commerce_key', item.comercio]);
		//     const commerce = _.find(order.commerces, ['commerce_key', item.comercio]);
		//     if (commerce) {
		//       if (item.tipo_descuento === 'porcentaje') {
		//         order.descuento += (item.descuento / 100 * commerce.total);
		//         order.commerces[commerce_index].descuento += (item.descuento / 100 * commerce.total);
		//       }

		//       if (item.tipo_descuento === 'numero') {
		//         order.descuento += item.descuento;
		//         order.commerces[commerce_index].descuento += item.descuento;
		//       }
		//     }
		//   })

		//   order.total -= order.descuento;
		//   order.total_compra -= order.descuento;
		// }

		const couponForRecadero = _.find(order.coupons, ['isForRecadero', true]);
		if (couponForRecadero) {
			// // console.log("CUPON RECADERO",couponForRecadero);
			if (couponForRecadero.tipo_descuento === 'porcentaje') {
				const descuento = parseFloat(((((fechaRepartos.length === 0 ? 1 : fechaRepartos.length) * 2.95) * couponForRecadero.descuento / 100)).toFixed(2));
				//// console.log("descuento",descuento);

				order.gastos_envio -= descuento;
				//// console.log("gastos_envio porcentaje", order.gastos_envio);

				order.descuento = descuento;
				// // console.log("decuento porcentaje", order.descuento);

				// // console.log("CUPON RECADERO porcentaje",order);
			}
			if (couponForRecadero.tipo_descuento === 'numero') {
				order.gastos_envio -= couponForRecadero.descuento;
				// // console.log("gastos_envio numero", order.gastos_envio);

				order.descuento = couponForRecadero.descuento;
				// // console.log("decuento numero", order.descuento);

				// // console.log("CUPON RECADERO numero",order);
			}
		} else {
			order.gastos_envio = (fechaRepartos.length === 0 ? 1 : fechaRepartos.length) * 2.95
			// // console.log("gastos_envio sin cupon", order.gastos_envio);

			// // console.log("SIN CUPON",order);
		}

		if (order.total_compra < 85) {
			order.total += order.gastos_envio;
		} else {
			order.gastos_envio = 0;
		}


		if (order.balance) {
			order.total -= order.cantidad_balance;
		}
		order.total = parseFloat((order.total).toFixed(2));
		// console.log('SALES DE UPDATETOTAL', order.total);
		// // console.log('SALES DE UPDATETOTAL', order.total_compra);
		// // console.log('SALES DE UPDATETOTAL', order);
		// // console.log(fechaRepartos);

	}

	/**
	 * Guarda la observación de un producto. Si el producto todavía no tiene
	 * ninguna unidad en el carrito, se añade una (con la nota ya puesta) en
	 * vez de fallar — antes, guardar una nota sin haber pulsado "+" primero
	 * mostraba un aviso de "añade una unidad" y no guardaba nada.
	 * Devuelve false si no se pudo (producto inactivo o sin stock), para que
	 * el componente que llama sepa si debe mostrar el aviso de éxito.
	 */
	updateNote(order: IOrder, commerce: ICommerce, product: IProduct): boolean {
		const _order: IOrder = JSON.parse(JSON.stringify(order));
		const commerceOrderIndex = _.findIndex(_order.commerces, ['commerce_key', commerce.key]);
		const existingConceptIndex = commerceOrderIndex !== -1
			? _.findIndex(_order.commerces[commerceOrderIndex].conceptos, ['product_key', product.key])
			: -1;
		const alreadyInCart = existingConceptIndex !== -1;

		if (!alreadyInCart) {
			if (product.active === false) {
				Swal.fire({
					title: 'Producto no disponible',
					text: 'Este producto ya no está disponible.',
					icon: 'warning'
				});
				return false;
			}
			if (!product.stock || product.stock < 1) {
				Swal.fire({
					title: 'Sin stock',
					text: 'Este producto no tiene unidades disponibles.',
					icon: 'warning'
				});
				return false;
			}
		}

		if (commerceOrderIndex !== -1) {
			const commerceOrder: ICommerceOrder = _order.commerces[commerceOrderIndex];
			if (alreadyInCart) {
				// No solo "if (product.observaciones)": eso impedía guardar que el
				// usuario había BORRADO una observación ya guardada (string vacío
				// es falsy, así que la nota antigua se quedaba pegada en el pedido).
				const concept = commerceOrder.conceptos[existingConceptIndex];
				concept.observaciones = product.observaciones || '';
				commerceOrder.conceptos[existingConceptIndex] = new Concept(concept);
			} else {
				commerceOrder.conceptos.push(this.createConcept(product));
			}
		} else {
			const concept = this.createConcept(product);
			_order.commerces.push({
				horario: commerce.horario,
				commerce_key: commerce.key,
				email_comercio: commerce.email,
				nombre_comercio: commerce.nombre_comercio,
				conceptos: [concept],
				base_imponible: concept.base_imponible,
				importe_iva: concept.importe_iva,
				total: concept.total,
				id: this.utils.makeid(10),
				open: true,
				pedido_minimo: commerce.pedido_minimo,
				fecha_recogida: ''
			});
		}

		this.updateTotal(_order);
		const action = new SetOrderActions(_order);
		this.store.dispatch(action);
		localStorage.setItem('order', this.encoder.encode(JSON.stringify(_order)))
		return true;
	}

	getPublicImgUrl(img) {
		if (img.thumbnail !== '') {

			let url = img.url.split('%2F').join('/')
			url = url.replace(/(\/)?([^\/]*)$/, '$1thumb_$2')
			let finalUrl = ""
			const parts = url.split('/')

			for (let index = 0; index < parts.length; index++) {
				if (index < 7) {
					finalUrl += parts[index] + '/'
				} else {
					finalUrl += parts[index] + '%2F'
				}

			}
			return finalUrl
		} else {
			return img.url
		}

	}
}


// const Toast = Swal.mixin({
//   toast: true,
//   position: 'top-end',
//   showConfirmButton: false,
//   timer: 3000,
//   timerProgressBar: true,
//   didOpen: (toast) => {
//     toast.addEventListener('mouseenter', Swal.stopTimer)
//     toast.addEventListener('mouseleave', Swal.resumeTimer)
//   }
// })

// Toast.fire({
//   icon: 'success',
//   title: 'Signed in successfully'
// })
